import {
  Title,
  Stack,
  Group,
  Button,
  Skeleton,
  Table,
  Text,
  ActionIcon,
  Tooltip,
  Modal,
  TextInput,
  Center,
  ThemeIcon,
} from '@mantine/core';
import { useClipboard, useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { IconTrash, IconPlus, IconPencil, IconLock, IconCopy, IconCheck } from '@tabler/icons-react';
import { useCostCenters, useDiscardCostCenter, useUpdateCostCenter } from '@api/costCenters';
import { CostCenter } from '@types';
import AddCostCenterModal from './AddCostCenterModal';
import { useAuth } from '@providers/auth';

const LEVEL_INDENT: Record<number, number> = { 1: 0, 2: 20, 3: 40 };
const LEVEL_LABEL: Record<number, string> = { 1: 'Nivel 1', 2: 'Nivel 2', 3: 'Nivel 3' };

function buildTree(items: CostCenter[]): CostCenter[] {
  const level1 = items.filter((c) => c.level === 1);
  const level2 = items.filter((c) => c.level === 2);
  const level3 = items.filter((c) => c.level === 3);

  const result: CostCenter[] = [];
  for (const l1 of level1) {
    result.push(l1);
    for (const l2 of level2.filter((c) => c.parent_id === l1.id)) {
      result.push(l2);
      for (const l3 of level3.filter((c) => c.parent_id === l2.id)) {
        result.push(l3);
      }
    }
  }
  return result;
}

interface EditModalProps {
  item: CostCenter;
  opened: boolean;
  close: () => void;
}

function EditCostCenterModal({ item, opened, close }: EditModalProps) {
  const { mutate: update } = useUpdateCostCenter();
  const form = useForm({
    initialValues: { name: item.name },
    validate: { name: (v) => (!v ? 'El nombre es requerido' : null) },
  });

  function handleSubmit(values: typeof form.values) {
    update({ id: item.id!, name: values.name });
    close();
  }

  return (
    <Modal.Root size="sm" opened={opened} onClose={close} centered>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title fz={18} fw={600}>
            Editar — {LEVEL_LABEL[item.level]}
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              <TextInput label="Nombre" required {...form.getInputProps('name')} />
              <Button type="submit">Guardar</Button>
            </Stack>
          </form>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

function CostCenterRow({
  item,
  allItems,
  onAddChild,
  onEdit,
}: {
  item: CostCenter;
  allItems: CostCenter[];
  onAddChild: (parent: CostCenter) => void;
  onEdit: (item: CostCenter) => void;
}) {
  const { mutate: discard } = useDiscardCostCenter();
  const clipboard = useClipboard({ timeout: 1500 });
  const isDiscarded = !!item.discarded_at;
  const hasActiveChildren = allItems.some((c) => c.parent_id === item.id && !c.discarded_at);
  const canHaveChildren = item.level < 3;

  return (
    <Table.Tr style={{ opacity: isDiscarded ? 0.35 : 1 }}>
      <Table.Td>
        <Text fz="sm" pl={LEVEL_INDENT[item.level]}>
          {item.name}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text fz="sm" c="dimmed">
          {LEVEL_LABEL[item.level]}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" justify="flex-end">
          <Tooltip label={clipboard.copied ? 'Copiado' : `Copiar ID: ${item.id}`}>
            <ActionIcon
              variant="subtle"
              color={clipboard.copied ? 'teal' : 'gray'}
              size="sm"
              onClick={() => clipboard.copy(String(item.id))}
            >
              {clipboard.copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
          {!isDiscarded && (
            <Tooltip label="Editar nombre">
              <ActionIcon variant="light" color="gray" size="sm" onClick={() => onEdit(item)}>
                <IconPencil size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {!isDiscarded && canHaveChildren && (
            <Tooltip label={`Agregar nivel ${item.level + 1} en "${item.name}"`}>
              <ActionIcon variant="light" color="blue" size="sm" onClick={() => onAddChild(item)}>
                <IconPlus size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {!isDiscarded && (
            <Tooltip
              label={
                hasActiveChildren ? 'Desactiva primero los planes de cuenta hijos' : 'Desactivar'
              }
            >
              <ActionIcon
                variant="light"
                color="red"
                size="sm"
                disabled={hasActiveChildren}
                onClick={() => discard(item.id!)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function CostCentersPage() {
  const { session } = useAuth();
  const available = !!session.activeAccount?.cost_centers_available;
  const { data, isFetching } = useCostCenters();
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [pendingParent, setPendingParent] = useState<CostCenter | null>(null);
  const [editingItem, setEditingItem] = useState<CostCenter | null>(null);

  const allItems = data?.data ?? [];
  const tree = buildTree(allItems);

  if (!available) {
    return (
      <Center mt={80}>
        <Stack align="center" gap="md">
          <ThemeIcon size={64} radius="xl" variant="light" color="gray">
            <IconLock size={32} />
          </ThemeIcon>
          <Title order={3} c="dimmed">
            Planes de cuenta no habilitados
          </Title>
          <Text c="dimmed" ta="center" maw={400}>
            Esta funcionalidad no está habilitada para tu cuenta. Contacta al administrador del
            sistema para activarla.
          </Text>
        </Stack>
      </Center>
    );
  }

  function handleAddL1() {
    setPendingParent(null);
    openAdd();
  }

  function handleAddChild(parent: CostCenter) {
    setPendingParent(parent);
    openAdd();
  }

  function handleCloseAdd() {
    closeAdd();
    setPendingParent(null);
  }

  function handleEdit(item: CostCenter) {
    setEditingItem(item);
    openEdit();
  }

  function handleCloseEdit() {
    closeEdit();
    setEditingItem(null);
  }

  const fixedLevel = pendingParent ? ((pendingParent.level + 1) as 2 | 3) : 1;

  return (
    <>
      <Stack maw={700} mx="auto" mt="md">
        <Group justify="space-between">
          <Title order={1}>Planes de cuenta</Title>
          <Button onClick={handleAddL1}>Agregar nivel 1</Button>
        </Group>

        {isFetching ? (
          <Skeleton height={120} />
        ) : (
          <Table horizontalSpacing="md" verticalSpacing="sm" highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nombre</Table.Th>
                <Table.Th>Nivel</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tree.length > 0 ? (
                tree.map((item) => (
                  <CostCenterRow
                    key={item.id}
                    item={item}
                    allItems={allItems}
                    onAddChild={handleAddChild}
                    onEdit={handleEdit}
                  />
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={3}>No hay planes de cuenta configurados</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <AddCostCenterModal
        opened={addOpened}
        close={handleCloseAdd}
        fixedLevel={fixedLevel}
        fixedParentId={pendingParent?.id}
        parentName={pendingParent?.name}
      />

      {editingItem && (
        <EditCostCenterModal item={editingItem} opened={editOpened} close={handleCloseEdit} />
      )}
    </>
  );
}

export default CostCentersPage;
